import React, { useState, useEffect, useMemo } from "react";
import { useDataEngine } from '@dhis2/app-runtime';
import {
  CircularLoader,
  NoticeBox,
  InputField,
  Button,
  Modal,
  ModalTitle,
  ModalContent,
  ModalActions,
  Chip,
  IconView24,
  colors,
  Checkbox
} from '@dhis2/ui';
import { getCurrentUserWithRole } from '../objectivesWeeklyReporting/utils/userRoleUtils'

// Data Element Mappings for Tasks program stage
const TASK_DATAELEMENT_MAPPING = {
  selectedWeek: 'EiL3lPaL908',
  progressPercent: 'AElpNxzTdRh',
  achievements: 'j9FdNkmPpXM',
  challenges: 'WSQ9nuXPnNL',
  objectiveId: 'plJovUlxfQ0',
  taskName: 'I0QtZkMbzxN',
  taskDescription: 'LR70eqTjB8m',
};

const TASK_PROGRAM_STAGE_ID = 'Nv9nYNcBTdM';
const PROGRAM_ID = 'yiT00XKrrHP';

// Helper: Get color based on progress percentage (matching WeeklyProgressTracker)
const getProgressColor = (progress) => {
  if (progress >= 67) return '#28a745'; // Green
  if (progress >= 34) return '#ffc107'; // Amber/Yellow
  return '#dc3545'; // Red
};

// Helper: Get background gradient for progress bar
const getProgressBackground = (progress) => {
  const color = getProgressColor(progress);
  return `linear-gradient(to right, ${color} 0%, ${color} ${progress}%, #e9ecef ${progress}%, #e9ecef 100%)`;
};

const TasksPage = () => {
  const engine = useDataEngine();
  const [currentUser, setCurrentUser] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [existingTasks, setExistingTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMounted, setIsMounted] = useState(true);
  const [error, setError] = useState(null);
  
  // New state for filtering
  const [showOnlyUncompleted, setShowOnlyUncompleted] = useState(false);
  const [sortBy, setSortBy] = useState('week-desc'); // 'week-desc', 'progress-asc', 'progress-desc'

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userInfo = await getCurrentUserWithRole(engine);
        if (isMounted) {
          setCurrentUser(userInfo);
        }
      } catch (error) {
        if (isMounted) {
          setError(error.message);
          setIsLoading(false);
        }
      }
    };
    fetchUser();
  }, [engine, isMounted]);

  // Load tasks for the current user only
  useEffect(() => {
    const loadUserTasks = async () => {
      if (!currentUser) return;
      setIsLoading(true);

      try {
        const currentUsername = currentUser.username || currentUser.userCredentials?.username;
        if (!currentUsername) {
          if (isMounted) {
            setExistingTasks([]);
            setIsLoading(false);
          }
          return;
        }

        const response = await engine.query({
          tasks: {
            resource: 'events',
            params: {
              program: PROGRAM_ID,
              programStage: TASK_PROGRAM_STAGE_ID,
              fields: 'event,orgUnitName,createdBy,createdByUserInfo,dataValues[dataElement,value]',
              pageSize: 1000,
              order: `${TASK_DATAELEMENT_MAPPING.selectedWeek}:desc`
            }
          }
        });

        const userTasks = response.tasks?.events?.filter(event => {
          return event.createdBy === currentUsername || 
                 event.createdByUserInfo?.username === currentUsername;
        }) || [];

        const tasks = userTasks.map(event => {
          const dataValues = event.dataValues || [];
          const progressPercent = parseInt(dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.progressPercent)?.value) || 0;
          return {
            id: event.event,
            orgUnitName: event.orgUnitName,
            createdBy: event.createdBy,
            createdByUserInfo: event.createdByUserInfo,
            weekEnding: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.selectedWeek)?.value || 'Unknown Week',
            progressPercent: progressPercent,
            isCompleted: progressPercent === 100,
            challenges: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.challenges)?.value || '',
            achievements: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.achievements)?.value || '',
            taskName: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskName)?.value || 'Unnamed Task',
            taskDescription: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskDescription)?.value || ''
          };
        });

        if (isMounted) {
          setExistingTasks(tasks || []);
          setIsLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          setError(error.message);
          setExistingTasks([]);
          setIsLoading(false);
        }
      }
    };

    if (currentUser) {
      loadUserTasks();
    }
  }, [currentUser, engine, isMounted]);

  // Filtered and sorted tasks with memoization
  const filteredTasks = useMemo(() => {
    let result = [...existingTasks];
    
    // Apply search filter
    if (searchTerm) {
      result = result.filter(task => 
        task.taskName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.taskDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.orgUnitName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply uncompleted filter
    if (showOnlyUncompleted) {
      result = result.filter(task => !task.isCompleted);
    }
    
    // Apply sorting
    result.sort((a, b) => {
      if (sortBy === 'week-desc') {
        return new Date(b.weekEnding) - new Date(a.weekEnding);
      } else if (sortBy === 'progress-asc') {
        return a.progressPercent - b.progressPercent;
      } else if (sortBy === 'progress-desc') {
        return b.progressPercent - a.progressPercent;
      }
      return 0;
    });
    
    return result;
  }, [existingTasks, searchTerm, showOnlyUncompleted, sortBy]);

  // Task statistics
  const taskStats = useMemo(() => {
    const total = existingTasks.length;
    const completed = existingTasks.filter(t => t.isCompleted).length;
    const uncompleted = total - completed;
    const avgProgress = total > 0 
      ? Math.round(existingTasks.reduce((sum, t) => sum + t.progressPercent, 0) / total) 
      : 0;
    
    return { total, completed, uncompleted, avgProgress };
  }, [existingTasks]);

  const handleCloseTaskModal = () => {
    setIsTaskModalOpen(false);
    setSelectedTask(null);
  };

  const TaskDetailModal = ({ task, onClose }) => {
    if (!task) return null;
    const progressColor = getProgressColor(task.progressPercent);
    
    return (
      <Modal large onClose={onClose}>
        <ModalTitle>Task Details</ModalTitle>
        <ModalContent>
          <div style={{ padding: '16px' }}>
            {/* Header with progress indicator */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: colors.grey100,
              borderRadius: '8px'
            }}>
              <h3 style={{ margin: 0, color: '#2c3e50' }}>{task.taskName}</h3>
              <Chip 
                backgroundColor={progressColor} 
                color="#fff"
                style={{ fontWeight: 'bold' }}
              >
                {task.progressPercent}% {task.isCompleted ? '✓ Complete' : 'In Progress'}
              </Chip>
            </div>
            
            {/* Visual Progress Bar */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '4px',
                fontSize: '0.9rem',
                color: colors.grey700
              }}>
                <span>Overall Progress</span>
                <span style={{ fontWeight: 'bold', color: progressColor }}>
                  {task.progressPercent}%
                </span>
              </div>
              <div style={{ 
                height: '20px', 
                backgroundColor: '#e9ecef', 
                borderRadius: '10px',
                overflow: 'hidden',
                border: '1px solid #ddd'
              }}>
                <div style={{ 
                  width: `${task.progressPercent}%`,
                  height: '100%',
                  background: getProgressBackground(task.progressPercent),
                  borderRadius: '10px',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div>
                <strong>Week Ending:</strong> 
                <div style={{ marginTop: '4px', padding: '6px 10px', backgroundColor: colors.grey100, borderRadius: '4px', display: 'inline-block' }}>
                  {task.weekEnding}
                </div>
              </div>
              <div>
                <strong>Status:</strong>
                <div style={{ marginTop: '4px' }}>
                  <Chip 
                    backgroundColor={task.isCompleted ? colors.green200 : colors.yellow200}
                    color={task.isCompleted ? colors.green900 : colors.yellow900}
                  >
                    {task.isCompleted ? 'Completed' : 'In Progress'}
                  </Chip>
                </div>
              </div>
              <div>
                <strong>Department:</strong> 
                <div style={{ marginTop: '4px', color: colors.grey700 }}>{task.orgUnitName}</div>
              </div>
            </div>
            
            <div style={{ height: '1px', backgroundColor: colors.grey300, margin: '16px 0' }} />
            
            {task.taskDescription && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Description:</strong> 
                <div style={{ marginTop: '8px', padding: '12px', backgroundColor: colors.grey100, borderRadius: '6px', borderLeft: '4px solid #2980b9' }}>
                  {task.taskDescription}
                </div>
              </div>
            )}
            
            {task.achievements && (
              <div style={{ marginBottom: '16px' }}>
                <strong>✅ Achievements / Milestone:</strong>
                <div style={{ marginTop: '8px', padding: '12px', backgroundColor: colors.green050, borderRadius: '6px', borderLeft: '4px solid #28a745' }}>
                  {task.achievements}
                </div>
              </div>
            )}
            
            {task.challenges && (
              <div style={{ marginBottom: '16px' }}>
                <strong>⚠️ Challenges / Risks:</strong>
                <div style={{ marginTop: '8px', padding: '12px', backgroundColor: colors.yellow050, borderRadius: '6px', borderLeft: '4px solid #ffc107' }}>
                  {task.challenges}
                </div>
              </div>
            )}
          </div>
        </ModalContent>
        <ModalActions>
          <Button onClick={onClose}>Close</Button>
        </ModalActions>
      </Modal>
    );
  };

  // Progress Badge Component for task cards
  const ProgressBadge = ({ progress, isCompleted }) => {
    const color = getProgressColor(progress);
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '6px',
        padding: '4px 10px',
        backgroundColor: color,
        color: '#fff',
        borderRadius: '20px',
        fontSize: '0.85rem',
        fontWeight: 'bold',
        minWidth: '70px',
        justifyContent: 'center'
      }}>
        {isCompleted ? '✓' : `${progress}%`}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div style={{ padding: 16, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularLoader />
        <span style={{ marginLeft: 8 }}>Loading your tasks...</span>
      </div>
    );
  }

  if (error) return <NoticeBox title="Error loading tasks">{error}</NoticeBox>;

  return (
    <div style={{ padding: 16, maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header Section */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 20, 
        flexWrap: 'wrap', 
        gap: '16px' 
      }}>
        <h3 style={{ margin: 0, color: '#297934ff' }}>My Tasks</h3>
        
        {/* Stats Overview */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Chip backgroundColor={colors.blue100} color={colors.blue900}>
            Total: {taskStats.total}
          </Chip>
          <Chip backgroundColor={colors.green100} color={colors.green900}>
            ✓ Completed: {taskStats.completed}
          </Chip>
          <Chip backgroundColor={colors.yellow100} color={colors.yellow900}>
            ⏳ In Progress: {taskStats.uncompleted}
          </Chip>
          <Chip backgroundColor={colors.grey200} color={colors.grey800} style={{ fontWeight: 'large' }}>
            Overall Average: {taskStats.avgProgress}%
          </Chip>
        </div>
      </div>

      {/* Filters and Controls */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: 20, 
        flexWrap: 'wrap',
        padding: '12px 16px',
        backgroundColor: colors.grey100,
        borderRadius: '8px',
        alignItems: 'center'
      }}>
        <InputField
          placeholder="🔍 Search tasks..."
          value={searchTerm}
          onChange={({ value }) => setSearchTerm(value)}
          style={{ width: 250, minWidth: '200px' }}
        />
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Checkbox
            checked={showOnlyUncompleted}
            onChange={({ checked }) => setShowOnlyUncompleted(checked)}
            label="Show only in-progress"
          />
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
          <span style={{ fontSize: '0.9rem', color: colors.grey700 }}>Sort by:</span>
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            style={{ 
              padding: '8px 12px', 
              borderRadius: '4px', 
              border: `1px solid ${colors.grey500}`,
              backgroundColor: '#fff',
              cursor: 'pointer'
            }}
          >
            <option value="week-desc">Week (Newest)</option>
            <option value="progress-asc">Progress (Low to High)</option>
            <option value="progress-desc">Progress (High to Low)</option>
          </select>
        </div>
        
        <Button primary small onClick={() => window.location.reload()}>
          ↻ Refresh
        </Button>
      </div>
      
      {/* Active Filters Display */}
      {(searchTerm || showOnlyUncompleted) && (
        <div style={{ 
          marginBottom: '16px', 
          display: 'flex', 
          gap: '8px', 
          flexWrap: 'wrap' 
        }}>
          {searchTerm && (
            <Chip 
              onClick={() => setSearchTerm('')} 
              style={{ cursor: 'pointer' }}
              backgroundColor={colors.blue100}
            >
              Search: "{searchTerm}" ×
            </Chip>
          )}
          {showOnlyUncompleted && (
            <Chip 
              onClick={() => setShowOnlyUncompleted(false)} 
              style={{ cursor: 'pointer' }}
              backgroundColor={colors.yellow100}
            >
              In Progress Only ×
            </Chip>
          )}
        </div>
      )}
      
      {filteredTasks.length === 0 ? (
        <NoticeBox title="No Tasks Found" info>
          {searchTerm ? 
            `No tasks found matching "${searchTerm}"` : 
            showOnlyUncompleted ?
            "All your tasks are completed! 🎉" :
            "You haven't created any tasks yet."
          }
        </NoticeBox>
      ) : (
        <>
          {/* Group tasks by week */}
          {Object.entries(
            filteredTasks.reduce((acc, task) => {
              const week = task.weekEnding || 'Unknown Week';
              if (!acc[week]) acc[week] = [];
              acc[week].push(task);
              return acc;
            }, {})
          )
          .sort(([weekA], [weekB]) => new Date(weekB) - new Date(weekA))
          .map(([week, weekTasks]) => (
            <div key={week} style={{ marginBottom: '32px' }}>
              <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                margin: "0 0 16px 0",
                padding: '8px 12px',
                backgroundColor: colors.grey100,
                borderRadius: '8px 8px 0 0',
                borderBottom: '2px solid #297934ff'
              }}>
                <h3 style={{ margin: 0, color: '#297934ff' }}>
                  Week Ending: {week}
                </h3>
                <Chip backgroundColor={colors.grey200}>
                  {weekTasks.length} task{weekTasks.length !== 1 ? 's' : ''}
                </Chip>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {weekTasks.map((task) => {
                  const progressColor = getProgressColor(task.progressPercent);
                  const isCompleted = task.progressPercent === 100;
                  
                  return (
                    <div 
                      key={task.id} 
                      style={{ 
                        padding: "16px", 
                        backgroundColor: isCompleted ? colors.green050 : "#f8f9fa", 
                        borderRadius: "8px",
                        borderLeft: `4px solid ${progressColor}`,
                        border: `1px solid ${isCompleted ? colors.green200 : colors.grey300}`,
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setSelectedTask(task);
                        setIsTaskModalOpen(true);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        gap: '16px'
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '12px',
                            marginBottom: '8px',
                            flexWrap: 'wrap'
                          }}>
                            <strong style={{ 
                              fontSize: '1.1rem', 
                              color: isCompleted ? colors.green900 : '#2c3e50',
                              flex: 1,
                              minWidth: '200px'
                            }}>
                              {isCompleted && '✓ '}{task.taskName || 'Unnamed Task'}
                            </strong>
                            <ProgressBadge 
                              progress={task.progressPercent} 
                              isCompleted={isCompleted} 
                            />
                          </div>
                          
                          {/* Visual Progress Bar in Card */}
                          <div style={{ 
                            height: '8px', 
                            backgroundColor: '#e9ecef', 
                            borderRadius: '4px',
                            overflow: 'hidden',
                            marginBottom: '8px'
                          }}>
                            <div style={{ 
                              width: `${task.progressPercent}%`,
                              height: '100%',
                              background: getProgressBackground(task.progressPercent),
                              borderRadius: '4px',
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                          
                          <div style={{ 
                            display: 'flex', 
                            gap: '16px', 
                            fontSize: '0.9rem', 
                            color: colors.grey700,
                            flexWrap: 'wrap'
                          }}>
                            <span>📅 {task.weekEnding}</span>
                            <span>🏢 {task.orgUnitName}</span>
                            {task.achievements && (
                              <span style={{ color: colors.green700 }}>✅ Has achievements</span>
                            )}
                            {task.challenges && (
                              <span style={{ color: colors.yellow800 }}>⚠️ Has challenges</span>
                            )}
                          </div>
                        </div>
                        
                        <Button 
                          small 
                          primary
                          onClick={(e) => {
                              // Safely stop propagation if event supports it
                              if (e?.stopPropagation) {
                                e.stopPropagation();
                              }
                              setSelectedTask(task);
                              setIsTaskModalOpen(true);
                            }}
                          style={{ 
                            backgroundColor: isCompleted ? colors.green600 : undefined,
                            borderColor: isCompleted ? colors.green700 : undefined
                          }}
                        >
                          <IconView24 style={{ marginRight: '4px' }} />
                          View
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}

      {/* Footer Stats */}
      {filteredTasks.length > 0 && (
        <div style={{ 
          marginTop: 24, 
          padding: '12px 16px',
          backgroundColor: colors.grey100,
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <span style={{ color: colors.grey700 }}>
            Showing <strong>{filteredTasks.length}</strong> of <strong>{existingTasks.length}</strong> tasks
          </span>
          {showOnlyUncompleted && (
            <span style={{ color: colors.yellow800, fontWeight: '500' }}>
              ⏳ Filtering: In-progress tasks only
            </span>
          )}
        </div>
      )}

      {isTaskModalOpen && (
        <TaskDetailModal
          task={selectedTask}
          onClose={handleCloseTaskModal}
        />
      )}
    </div>
  );
};

export default TasksPage;